// "Open this desk in your browser": where a copyable desk link goes.
//
// A desk shared as a snapshot with scope 'copy' says anyone who wants it may
// have it. Until this, having it meant signing up here and then installing the
// desktop app to find it in an inbox -- a dead end for someone who has just
// been sent a desk and has no Plexii at all. The share app on plexiidesk.com
// (focusbuddy src/web) now opens the same token at <share app>/c/<token> as a
// working desk in the browser, with no account and no install, exactly as it
// opens a 'use' link at /s/<token>.
//
// Only a desk ('task') with scope 'copy' gets the button: a 'view' link is the
// sender saying "read, don't copy", and the share app refuses it too. And only
// a token of the shape the desktop mints (16 letters of its alphabet), which is
// the only shape the share app reads at /c/ -- anything else would send the
// visitor to a page that cannot open it.
//
// Dependency-free, so the Node tests load it as written. The caller still runs
// the result through safeHref, as every href on this page is.

/** The share app on the marketing site. Override per build with VITE_SHARE_APP_URL. */
export const DEFAULT_SHARE_APP_URL = 'https://plexiidesk.com/share'

/** A read token exactly as the desktop mints one (focusbuddy lib/shareTokens.ts). */
export const READ_TOKEN = /^[23456789abcdefghjkmnpqrstuvwxyz]{16}$/

/** Does this share get "Open this desk in your browser"? */
export function canOpenInBrowser(kind: unknown, scope: unknown, token: unknown): boolean {
  return kind === 'task' && scope === 'copy' && typeof token === 'string' && READ_TOKEN.test(token)
}

/**
 * The share app's address for this token, or null when there is none to give:
 * a token of another shape, or a base that is not an https (or local http)
 * address with no credentials in it.
 */
export function openInBrowserUrl(shareAppBase: string | undefined, token: string): string | null {
  if (!READ_TOKEN.test(token)) return null
  let base: URL
  try {
    base = new URL((shareAppBase || DEFAULT_SHARE_APP_URL).trim())
  } catch {
    return null
  }
  const local = base.hostname === 'localhost' || base.hostname === '127.0.0.1'
  if (base.protocol !== 'https:' && !(local && base.protocol === 'http:')) return null
  if (base.username || base.password) return null
  return `${base.origin}${base.pathname.replace(/\/+$/, '')}/c/${token}`
}
